import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GradientBackground from '../components/GradientBackground';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <>
      <GradientBackground backgroundIndex={0} />
      <div style={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        zIndex: 1,
        overflow: 'hidden',
      }}>
        {/* Navigation */}
        <Navigation />

        {/* Privacy Policy Content */}
        <section style={{
          minHeight: '100vh',
          padding: '80px 24px',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(20px)',
          position: 'relative',
        }}
        className="privacy-policy-section"
        >
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
          }}>
            {/* Back Button */}
            <button
              onClick={handleBackToHome}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                marginBottom: '40px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              {t('common.backToHome')}
            </button>

            {/* Privacy Policy Content */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '24px',
              padding: '48px',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(20px)',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
            className="privacy-policy-content"
            >
              <h1 style={{
                fontSize: '36px',
                fontWeight: '600',
                color: 'white',
                marginBottom: '24px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                textAlign: 'center',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
              className="privacy-policy-title"
              >
                {t('privacy.title')}
              </h1>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.7)',
                marginBottom: '32px',
                textAlign: 'center',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              }}>
                {t('common.lastUpdated')}
              </p>

              <div style={{
                fontSize: '16px',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.7',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}>
                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('privacy.sections.infoWeCollect.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.infoWeCollect.accountInfo.label')}</strong> {t('privacy.sections.infoWeCollect.accountInfo.text')}
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.infoWeCollect.eventData.label')}</strong> {t('privacy.sections.infoWeCollect.eventData.text')}
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.infoWeCollect.eventImages.label')}</strong> {t('privacy.sections.infoWeCollect.eventImages.text')}
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.infoWeCollect.usageInfo.label')}</strong> {t('privacy.sections.infoWeCollect.usageInfo.text')}
                </p>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('privacy.sections.cameraPermission.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.cameraPermission.whyWeNeed.label')}</strong> {t('privacy.sections.cameraPermission.whyWeNeed.text')}
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.cameraPermission.howWeUse.label')}</strong>
                </p>
                <ul style={{ marginLeft: '20px', marginBottom: '16px' }}>
                  {(t('privacy.sections.cameraPermission.howWeUse.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                  ))}
                </ul>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.cameraPermission.dataRetention.label')}</strong> {t('privacy.sections.cameraPermission.dataRetention.text')}
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.cameraPermission.yourRights.label')}</strong> {t('privacy.sections.cameraPermission.yourRights.text')}
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.cameraPermission.thirdParty.label')}</strong> {t('privacy.sections.cameraPermission.thirdParty.text')}
                </p>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('privacy.sections.imageUpload.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.imageUpload.eventFlyers.label')}</strong> {t('privacy.sections.imageUpload.eventFlyers.text')}
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.imageUpload.eventAlbums.label')}</strong> {t('privacy.sections.imageUpload.eventAlbums.text')}
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.imageUpload.imageStorage.label')}</strong> {t('privacy.sections.imageUpload.imageStorage.text')}
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.imageUpload.imageAccess.label')}</strong> {t('privacy.sections.imageUpload.imageAccess.text')}
                </p>
                <ul style={{ marginLeft: '20px', marginBottom: '16px' }}>
                  {(t('privacy.sections.imageUpload.imageAccess.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.imageUpload.imageDeletion.label')}</strong> {t('privacy.sections.imageUpload.imageDeletion.text')}
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong>{t('privacy.sections.imageUpload.imageRetention.label')}</strong> {t('privacy.sections.imageUpload.imageRetention.text')}
                </p>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('privacy.sections.howWeUse.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('privacy.sections.howWeUse.text')}
                </p>
                <ul style={{ marginLeft: '20px', marginBottom: '16px' }}>
                  {(t('privacy.sections.howWeUse.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('privacy.sections.informationSharing.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('privacy.sections.informationSharing.text')}
                </p>
                <ul style={{ marginLeft: '20px', marginBottom: '16px' }}>
                  {(t('privacy.sections.informationSharing.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                  ))}
                </ul>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('privacy.sections.dataSecurity.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('privacy.sections.dataSecurity.text')}
                </p>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('privacy.sections.yourRights.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('privacy.sections.yourRights.text')}
                </p>

                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: 'white', 
                  marginBottom: '16px', 
                  marginTop: '32px',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}>
                  {t('privacy.sections.contactUs.title')}
                </h2>
                <p style={{ marginBottom: '16px' }}>
                  {t('privacy.sections.contactUs.text')} <a 
                    href={`mailto:${t('privacy.sections.contactUs.email')}`}
                    style={{
                      color: 'white',
                      textDecoration: 'none',
                    }}
                  >
                    {t('privacy.sections.contactUs.email')}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;
